import {
  Flex,
  HStack,
  Icon,
  Switch,
  type SwitchProps,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { trpcAdmin, type RouterOutputs } from '../lib/trpcAdmin';
import { BadgeCheck } from 'lucide-react';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ProviderCourseURLParams } from './types';
import { testIds } from './ProviderCoursesTable.testIds';
import { SortOrder } from '@duke-hq/lib/pagination';
import { isCourseStatusAssignable } from '@duke-hq/shared-constants/course';
import { getCourseKindLabel } from '../lib/courseUtils';

export function ProviderCoursesTable({
  courses,
  urlParams,
  onFilters,
}: {
  courses: NonNullable<RouterOutputs['providers']['courses']>['items'];
  urlParams: ProviderCourseURLParams;
  onFilters: (filters: ProviderCourseURLParams) => void;
}) {
  const { orderBy, sort } = urlParams;
  const { providerId } = useParams();
  const tenant = Number(providerId);
  const [items, setSortedItems] = useState(courses);
  const toast = useToast();
  const utils = trpcAdmin.useUtils();

  const insertProviderCourse = trpcAdmin.providers.addCourseToProvider.useMutation({
    onSuccess: async () => {
      toast({
        title: 'Provider course updated successfully.',
        description: 'Provider course updated successfully',
        status: 'success',
        duration: 7000,
        isClosable: true,
      });

      await utils.providers.courses.refetch();
    },
  });

  const handleSort = useCallback(
    (name: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      const updatedFilters = {
        orderBy: name,
        sort: newSortDirection,
      } as ProviderCourseURLParams;

      onFilters({ ...updatedFilters });
    },
    [onFilters, sort],
  );

  const handleSwitchChange = async (id: string, fieldName: string, newValue: boolean) => {
    let updatedItem;

    setSortedItems((items) =>
      items.map((item) => {
        if (item.id === id) {
          updatedItem = {
            ...item,
            tenantCourses: {
              ...item.tenantCourses,
              [fieldName]: newValue,
            },
          };
          return updatedItem;
        }
        return item;
      }),
    );

    if (!updatedItem) {
      return;
    }

    const payload = {
      tenant_id: tenant,
      course_id: updatedItem.id,
      is_available: updatedItem.tenantCourses.is_available ?? false,
      internal_enrolments: updatedItem.tenantCourses.internal_enrolments ?? false,
      active_from: updatedItem.tenantCourses.active_from ?? null,
      active_to: updatedItem.tenantCourses.active_to ?? null,
    };

    try {
      await insertProviderCourse.mutateAsync(payload);
    } catch {
      toast({
        title: t`Provider course couldn't be updated.`,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    }
  };

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>
            <SortableHeader
              label={t`Title`}
              columnName="title"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Kind`}
              columnName="kind"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Accredited`}
              columnName="accredited"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Status`}
              columnName="status"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <Flex justify="space-between" align="center">
              <HStack spacing="5">
                <Text>
                  <Trans>Participant enrolments</Trans>
                </Text>
              </HStack>
            </Flex>
          </Th>
          <Th>
            <Flex justify="space-between" align="center">
              <HStack spacing="5">
                <Text>
                  <Trans>Internal enrolments</Trans>
                </Text>
              </HStack>
            </Flex>
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {Array.isArray(items) && items.length
          ? items.map((course) => {
              const isAssignable = isCourseStatusAssignable(course.status);
              const disabledReason = t`Inactive courses cannot be enabled`;
              return (
                <Tr key={course.id} _hover={{ bg: 'bg.subtle' }}>
                  <Td>
                    <Text fontWeight="medium">{course.title}</Text>
                  </Td>
                  <Td>
                    <Text fontWeight="medium">{getCourseKindLabel(course.kind)}</Text>
                  </Td>
                  <Td>
                    {course.accredited ? (
                      <Icon
                        as={BadgeCheck}
                        color="teal.500"
                        fill="teal.500"
                        stroke="fg.inverse"
                        boxSize="6"
                      />
                    ) : null}
                  </Td>
                  <Td>
                    <Tag borderRadius="full">
                      <Text fontSize="xs" color="success">
                        {course.status}
                      </Text>
                    </Tag>
                  </Td>
                  <Td>
                    <ToggleSwitch
                      checked={course.tenantCourses?.is_available}
                      isDisabled={!isAssignable && course.tenantCourses?.is_available !== true}
                      disabledReason={disabledReason}
                      onChange={async (newValue: boolean) => {
                        await handleSwitchChange(course.id, 'is_available', newValue);
                      }}
                      colorScheme="brand"
                      aria-label={t`Participant enrolment enabled switch`}
                      data-testid={testIds.participantEnrolmentSwitch}
                    />
                  </Td>
                  <Td>
                    <ToggleSwitch
                      checked={course.tenantCourses?.internal_enrolments}
                      isDisabled={
                        !isAssignable && course.tenantCourses?.internal_enrolments !== true
                      }
                      disabledReason={disabledReason}
                      onChange={async (newValue: boolean) => {
                        await handleSwitchChange(course.id, 'internal_enrolments', newValue);
                      }}
                      colorScheme="brand"
                      aria-label={t`Internal enrolment enabled switch`}
                      data-testid={testIds.internalEnrolmentSwitch}
                    />
                  </Td>
                </Tr>
              );
            })
          : null}
      </Tbody>
    </Table>
  );
}

function ToggleSwitch({
  checked,
  onChange,
  isDisabled,
  disabledReason,
  ...otherSwitchProps
}: Omit<SwitchProps, 'onChange' | 'checked'> & {
  checked: boolean | undefined;
  onChange: (newValue: boolean) => void;
  disabledReason?: string;
}) {
  const [isChecked, setIsChecked] = useState(checked);

  const internalSwitchOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setIsChecked(newValue);
    onChange(newValue);
  };

  const toggle = (
    <Switch
      isChecked={isChecked}
      isDisabled={isDisabled}
      onChange={internalSwitchOnChange}
      {...otherSwitchProps}
    />
  );

  if (isDisabled && disabledReason != null) {
    return (
      <Tooltip label={disabledReason} hasArrow>
        <span>{toggle}</span>
      </Tooltip>
    );
  }

  return toggle;
}
